import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CUSTOM_EMOJI,
  CUSTOM_GROUPS,
  EMOJI,
  EMOJI_GROUPS,
  searchCustom,
  searchEmoji,
  type CustomEmoji,
  type Emoji,
} from '@engineer-os/chat';
import { useStrings } from '../../state/useLocale';
import { CustomGlyph, StickerGlyph } from './CustomEmoji';

/** The table by character, so a recently used one keeps its real shortcode. */
const BY_CHAR = new Map<string, Emoji>([...CUSTOM_EMOJI, ...EMOJI].map((e) => [e.c, e]));

/** Ours carry an English name; Unicode's do not, and do not need one. */
const isOurs = (e: Emoji): e is CustomEmoji => 'name' in e;

/**
 * The palette: the fifty-eight this product drew for itself, then three
 * hundred and thirty-nine of Unicode's, with a search box over the top.
 *
 * Ours come first, and that is the whole reason they exist. Unicode has a
 * character for a rocket and nothing at all for a broken build, a fourth
 * rebase or a plaster on production, which are the things a room about this
 * product actually says; putting them at the top of the palette is what makes
 * them the obvious reply rather than a trick somebody has to be told about.
 * They are inserted as their own shortcode — `:shipit:` — so what lands in the
 * composer is the text that goes on the wire, and a learner who watches it
 * happen has learned how to type one without a palette.
 *
 * Two ways to find one, because people have two habits. Someone who knows what
 * they want types it — "rocket", "fire", "lgtm" — and the list narrows to
 * matches on the shortcode or any of the words beside it in the table. Someone
 * who does not know is browsing, and gets the groups in order with a row of
 * jump links above them, which is faster than scrolling three hundred cells
 * and much faster than deciding what a face is called.
 *
 * Recently used come first, in their own group, kept in this component and
 * lost when the panel is destroyed. Deliberately not a preference: a list of
 * emoji is not worth a write to storage on every message, and the eight most
 * recent are only useful inside one conversation anyway.
 *
 * The search field takes focus on open, so typing is the default action. The
 * grid is buttons, so Tab walks it and Enter picks; Escape is handled by the
 * composer above, which is what owns whether this is open.
 */
export interface EmojiPickerProps {
  recent: readonly string[];
  onPick(emoji: string): void;
  onClose(): void;
}

/** Every group the jump bar offers, ours first. */
const ALL_GROUPS = [...CUSTOM_GROUPS, ...EMOJI_GROUPS];

/**
 * A section of the grid as it is drawn.
 *
 * `id` is the group's English name and never changes; it is what the jump
 * links scroll to, and an anchor that moved when the language did would break
 * the bar for whichever language was not English. `name` is the heading in the
 * language the page is in — two fields, because one of them is for the machine
 * and the other is for a person.
 */
interface Section {
  readonly id: string;
  readonly name: string;
  readonly emoji: readonly Emoji[];
  /** Draw this section's cells on the wider track. A property of the section
   *  rather than something re-derived from its contents, because the search
   *  results are one section holding both kinds and there is no answer to
   *  "is this a sticker section" for that one. */
  readonly stickers?: true;
}

export function EmojiPicker({ recent, onPick, onClose }: EmojiPickerProps) {
  const s = useStrings().chat;
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const groups = useMemo<Section[]>(() => {
    const q = query.trim();
    // One list of results rather than two, because a search is one question.
    // Ours are in front of it for the same reason they are in front of the
    // palette: someone typing "ship" in this product means one of them. An
    // Arabic query works the same way: `searchCustom` lowercases and strips
    // the colons, and the Arabic words sit in the same key list as the
    // English ones (packages/chat/src/custom.ts).
    if (q !== '') return [{ id: 'results', name: s.emoji.results, emoji: [...searchCustom(q), ...searchEmoji(q)] }];
    const found: Section[] =
      recent.length > 0
        ? [{ id: 'recent', name: s.emoji.recent, emoji: recent.map((c) => BY_CHAR.get(c) ?? { c, code: c, keys: [] }) }]
        : [];
    return [
      ...found,
      ...ALL_GROUPS.map((g) => ({
        id: g.name,
        name: s.emoji.group(g),
        emoji: g.emoji,
        // `'sticker' in g` rather than a cast: ALL_GROUPS holds both kinds and
        // only ours carries the flag.
        ...('sticker' in g && g.sticker ? { stickers: true as const } : {}),
      })),
    ];
  }, [query, recent, s]);

  const empty = groups.every((g) => g.emoji.length === 0);

  return (
    <div
      ref={ref}
      className="emoji-pick"
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        // Ours, not the panel's: one Escape shuts the palette, a second shuts
        // the panel. Without this, reaching for an emoji and changing your
        // mind would close the whole conversation.
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="emoji-head">
        <input
          ref={searchRef}
          className="chat-input emoji-search"
          type="search"
          value={query}
          placeholder={s.emoji.search}
          aria-label={s.emoji.searchLabel}
          autoComplete="off"
          spellCheck={false}
          // Either script may be typed in here, so the field takes its
          // direction from the query rather than from the page.
          dir="auto"
          onChange={(e) => setQuery(e.target.value)}
        />
        {query === '' && (
          <nav className="emoji-jump" aria-label={s.emoji.groups}>
            {ALL_GROUPS.map((g) => {
              const first = g.emoji[0];
              const heading = s.emoji.group(g);
              return (
                <button
                  key={g.name}
                  type="button"
                  className="emoji-jump-btn"
                  title={heading}
                  // Keyed by the English name, which is the anchor's id and
                  // does not move when the language does.
                  onClick={() => ref.current?.querySelector(`#emoji-g-${g.name}`)?.scrollIntoView({ block: 'start' })}
                >
                  {first && isOurs(first) ? (
                    /* The jump bar wears the group's first entry, so the
                       Stickers button is a sticker — at the bar's own size,
                       which the stylesheet gives it. */
                    first.sticker ? (
                      <StickerGlyph emoji={first} labelled={false} />
                    ) : (
                      <CustomGlyph emoji={first} labelled={false} />
                    )
                  ) : (
                    <span className="emoji" aria-hidden="true">
                      {first?.c}
                    </span>
                  )}
                  <span className="sr-only">{heading}</span>
                </button>
              );
            })}
          </nav>
        )}
      </div>

      <div className="emoji-body scroll">
        {empty && <p className="chat-empty">{s.emoji.none}</p>}
        {groups.map((g) => (
          <section key={g.id} id={`emoji-g-${g.id}`} className="emoji-group">
            <h3 className="label emoji-group-name">{g.name}</h3>
            {/* The sticker drawer is the same grid on a wider track. A
                sticker in a 30px cell is a picture nobody can tell from
                another picture, which would make the one drawer where
                choosing is the whole interaction the one drawer you cannot
                choose in. */}
            <div className={g.stickers ? 'emoji-grid is-stickers' : 'emoji-grid'}>
              {g.emoji.map((e, i) =>
                isOurs(e) ? (
                  <button
                    key={`${e.c}-${i}`}
                    type="button"
                    className="emoji-cell"
                    title={s.emoji.cell(e)}
                    onClick={() => onPick(e.c)}
                  >
                    {e.sticker ? <StickerGlyph emoji={e} labelled={false} /> : <CustomGlyph emoji={e} labelled={false} />}
                    <span className="sr-only">{s.emoji.name(e)}</span>
                  </button>
                ) : (
                  /* A Unicode shortcode is what a learner types between two
                     colons, so it stays Latin and left to right in both
                     languages (docs/i18n.md §1). */
                  <button
                    key={`${e.c}-${i}`}
                    type="button"
                    className="emoji-cell"
                    title={`:${e.code}:`}
                    onClick={() => onPick(e.c)}
                  >
                    <span className="emoji" aria-hidden="true">
                      {e.c}
                    </span>
                    <span className="sr-only" dir="ltr">
                      {e.code}
                    </span>
                  </button>
                ),
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default EmojiPicker;
